import React from 'react';
import { Upload, Trash2, AlertCircle, Check } from 'lucide-react';
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface FileMetadata {
  id: string;
  file: File;
  preview?: string;
  displayName: string;
  error?: string;
}

interface MediaUploadDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Files to display in the dialog */
  files: FileMetadata[];
  /** Whether upload is in progress */
  isProcessing: boolean;
  /** Callback when file name changes */
  onFileNameChange: (fileId: string, newName: string) => void;
  /** Callback when file is removed */
  onFileRemove: (fileId: string) => void;
  /** Callback when upload is confirmed */
  onUploadConfirm: () => void;
  /** Callback when upload is cancelled */
  onUploadCancel: () => void;
  /** Function to get file icon based on MIME type */
  getFileIcon: (mimeType: string) => React.ReactNode;
  /** Function to format file size */
  formatFileSize: (bytes: number) => string;
}

/**
 * Media Upload Dialog Component
 * 
 * Displays a modern dialog for file upload preview and management.
 * Allows users to rename files, remove files, and confirm upload.
 */
export const MediaUploadDialog: React.FC<MediaUploadDialogProps> = ({
  open,
  onOpenChange,
  files,
  isProcessing,
  onFileNameChange,
  onFileRemove,
  onUploadConfirm,
  onUploadCancel,
  getFileIcon,
  formatFileSize
}) => {
  return (
    <CreateBaseDialog
      open={open}
      onClose={(refetch) => {
        if (!isProcessing) {
          onUploadCancel();
          onOpenChange(false);
        }
      }}
      icon={Upload}
      title="Dateien hochladen"
      description={`${files.length} ${files.length === 1 ? 'Datei' : 'Dateien'} zum Hochladen bereit`}
      hideSubmitButton={true}
      maxWidth="2xl"
      customFooterActions={
        <Button
          type="button"
          onClick={onUploadConfirm}
          disabled={
            isProcessing || 
            files.length === 0 || 
            files.some(fileMetadata => fileMetadata.error)
          }
          className={`h-11 px-8 text-white shadow-sm transition-all min-w-[160px] w-full sm:w-auto ${
            files.some(fileMetadata => fileMetadata.error)
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isProcessing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Wird hochgeladen...
            </>
          ) : files.some(fileMetadata => fileMetadata.error) ? (
            <>
              <AlertCircle className="h-4 w-4 mr-2" />
              Fehler beheben
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {files.length} {files.length === 1 ? 'Datei' : 'Dateien'} hochladen
            </>
          )}
        </Button>
      }
    >
      <div className="p-4 sm:p-5 md:p-6 overflow-y-auto">
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          {files.map((fileMetadata) => (
            <div
              key={fileMetadata.id}
              className={`relative p-3 sm:p-4 rounded-2xl border transition-all duration-200 group ${
                fileMetadata.error 
                  ? 'border-red-200 bg-red-50/30 hover:bg-red-50/50' 
                  : 'border-gray-100 bg-gray-50/30 hover:border-gray-200 hover:bg-gray-50/60 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)]'
              }`}
            >
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                {/* File Preview/Icon & Actions Header (Mobile) */}
                <div className="flex items-center justify-between w-full sm:w-auto">
                  <div className="flex-shrink-0 relative">
                    {fileMetadata.preview ? (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden border border-black/5 shadow-sm">
                        <img
                          src={fileMetadata.preview}
                          alt={fileMetadata.displayName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-white border border-black/5 flex items-center justify-center text-gray-400 shadow-sm">
                        {getFileIcon(fileMetadata.file.type)}
                      </div>
                    )}
                    {fileMetadata.error && (
                      <div className="absolute -top-1.5 -right-1.5 w-5.5 h-5.5 bg-red-500 rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                        <AlertCircle className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Actions (Mobile visible, Desktop hidden) */}
                  <div className="flex-shrink-0 sm:hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onFileRemove(fileMetadata.id)}
                      className="h-9 w-9 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full border border-transparent hover:border-red-100 transition-all"
                    >
                      <Trash2 className="h-4.5 w-4.5" />
                    </Button>
                  </div>
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0 w-full space-y-2 sm:space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 group/input">
                      <Input
                        value={(() => {
                          const lastDotIndex = fileMetadata.displayName.lastIndexOf('.');
                          return lastDotIndex > 0 ? fileMetadata.displayName.substring(0, lastDotIndex) : fileMetadata.displayName;
                        })()}
                        onChange={(e) => {
                          const lastDotIndex = fileMetadata.displayName.lastIndexOf('.');
                          const extension = lastDotIndex > 0 ? fileMetadata.displayName.substring(lastDotIndex) : '';
                          const newFullName = e.target.value + extension;
                          onFileNameChange(fileMetadata.id, newFullName);
                        }}
                        className={`h-9 sm:h-10 text-sm font-semibold transition-all px-3 border-gray-200/60 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 bg-white/50 hover:bg-white focus:bg-white shadow-sm rounded-xl ${
                          fileMetadata.error ? 'text-red-900 border-red-200 placeholder:text-red-300' : 'text-gray-900'
                        }`}
                        placeholder="Dateiname eingeben..."
                      />
                    </div>
                    {(() => {
                      const lastDotIndex = fileMetadata.displayName.lastIndexOf('.');
                      const extension = lastDotIndex > 0 ? fileMetadata.displayName.substring(lastDotIndex) : '';
                      return extension ? (
                        <span className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 bg-white border border-gray-100 rounded-lg shadow-sm flex-shrink-0">
                          {extension.replace('.', '')}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  
                  <div className="flex items-center gap-3 text-[12px] px-1">
                    <div className="flex items-center gap-1.5 text-gray-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400/60" />
                      {formatFileSize(fileMetadata.file.size)}
                    </div>
                    <span className="text-gray-300 text-lg">·</span>
                    <div className="text-gray-500 font-medium capitalize flex items-center gap-1.5">
                      {fileMetadata.file.type ? fileMetadata.file.type.split('/')[0] : 'Unbekannt'}
                    </div>
                  </div>
                  
                  {fileMetadata.error && (
                    <div className="flex items-center gap-2 mt-1 px-2 py-1.5 rounded-lg bg-red-50 border border-red-100/50">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                      <p className="text-[11px] text-red-700 font-semibold leading-tight">
                        {fileMetadata.error}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions (Desktop visible, Mobile hidden) */}
                <div className="flex-shrink-0 hidden sm:flex items-center h-full pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onFileRemove(fileMetadata.id)}
                    className="h-9 w-9 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all duration-200 rounded-full border border-transparent hover:border-red-100"
                  >
                    <Trash2 className="h-4.5 w-4.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CreateBaseDialog>
  );
};